import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import { videoInitialValues, VideoTabs } from 'components/organisms/forms/video/VideoForm';
import { listQueryKeys } from 'constants/queryKey';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiX } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { STATUS } from 'services/types';
import { usePostVideo } from 'services/video';
import { usePostVideoChunkedFile } from 'services/video/file';
import { IVideoMutationForm } from 'services/video/types';
import { onErrorShowToast } from 'utils/service/errorHandler';

const CreateVideo = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createVideo = usePostVideo();
  const addVideoChunkedFile = usePostVideoChunkedFile();
  const mutateOptions = useMutateOptions<IVideoMutationForm>();
  const queryClient = useQueryClient();

  const onGoToDetail = async (id: string) => {
    navigate(`/video/${id}/file`);
    await queryClient.invalidateQueries({
      queryKey: [listQueryKeys.videos],
    });
  };

  const onCreateVideoFile = async (file: File, videoId: string, version: number) => {
    await addVideoChunkedFile.mutateAsync(
      { file, videoId, version },
      {
        onError: async (error) => {
          onErrorShowToast(error, 'toast-message.add-file');
          await onGoToDetail(videoId);
        },
        onSuccess: async () => await onGoToDetail(videoId),
      },
    );
  };

  const onCreateVideo = async (
    values: IVideoMutationForm,
    helpers: FormikHelpers<IVideoMutationForm>,
  ) => {
    const video = {
      ...middleware.video.formToQuery(values),
      waitingForFile: !!values.file && values.status !== STATUS.draft,
    };

    await createVideo.mutateAsync(video, {
      ...mutateOptions(helpers),
      onSuccess: async (video) =>
        !!values.file
          ? await onCreateVideoFile(values.file, video.id, video.version)
          : await onGoToDetail(video.id),
    });
  };

  return (
    <Form.Video
      i18nextPrefix="video"
      initialValues={videoInitialValues}
      onSubmit={onCreateVideo}
      tittle={t('video.title.create')}
      disableTabs={[VideoTabs.contracts]}
      customBackItem={
        <Button isClear type="button" onlyIcon onClick={() => navigate('/video')}>
          <Button.Icon icon={<PiX />} />
        </Button>
      }
    />
  );
};

export default CreateVideo;
